/**
 * Text helpers for the screening engine: normalization, sentence splitting and
 * parsing of Czech number / money formats.
 */

/**
 * Lowercases and strips diacritics while keeping string length identical to the
 * NFC input, so indexes found in the folded text can be used to slice the original.
 */
export function fold(text: string): string {
    return Array.from(text.normalize("NFC"))
        .map((ch) => {
            const base = ch.normalize("NFD").replace(/[̀-ͯ]/g, "");
            return base.length === 1 ? base : ch;
        })
        .join("")
        .toLowerCase();
}

/** Normalizes whitespace produced by PDF text extraction (keeps double spaces as field separators). */
export function normalizeText(raw: string): string {
    return raw
        .normalize("NFC")
        .replace(/\r\n?/g, "\n")
        .replace(/[   ]/g, " ")
        .replace(/[–—]/g, "–")
        .replace(/[ \t]+\n/g, "\n")
        .trim();
}

/** Repairs tokens broken by PDF extraction, e.g. "firma - solutions.cz" or "jan @ firma.cz". */
export function compactTokens(text: string): string {
    return text.replace(/\s*@\s*/g, "@").replace(/(\w)\s*-\s*(\w)/g, "$1-$2");
}

/** Splits text into sentences / bullet items. */
export function splitSentences(text: string): string[] {
    return text
        .split(/\s*[•▪●\n]\s*|(?<=[.!?])\s+(?=[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ])/)
        .map((s) => s.replace(/\s+/g, " ").trim())
        .filter((s) => s.length >= 3);
}

const SCALE: Array<[RegExp, number]> = [
    [/^(mld|miliard)/, 1_000_000_000],
    [/^(mil|milion)/, 1_000_000],
    [/^(tis|tisic)/, 1_000],
];

/**
 * Parses a Czech formatted number: "4.200.000", "4 200 000", "4,2", "1 250,50".
 * Returns null for anything that is not a number.
 */
export function parseCzechNumber(raw: string): number | null {
    const value = raw.trim();
    if (!value) return null;
    if (/^\d{1,3}(?:[ .]\d{3})+(?:,\d+)?$/.test(value)) {
        return Number(value.replace(/[ .]/g, "").replace(",", "."));
    }
    if (/^\d+(?:[.,]\d+)?$/.test(value)) {
        return Number(value.replace(",", "."));
    }
    return null;
}

export function applyScale(amount: number, scaleWord: string | undefined): number {
    if (!scaleWord) return amount;
    const folded = fold(scaleWord);
    const match = SCALE.find(([re]) => re.test(folded));
    return match ? amount * match[1] : amount;
}

export interface MoneyMatch {
    value: number;
    currency: "CZK" | "EUR";
    index: number;
    /** Folded text preceding the amount, used to classify it (revenue, budget, ...). */
    context: string;
}

const MONEY_RE =
    /(\d{1,3}(?:[ .]\d{3})+(?:,\d+)?|\d+(?:[.,]\d+)?)\s*(mld\.?|miliard\w*|mil\.?|milion\w*|tis\.?|tisic\w*)?\s*(kc|czk|eur|€)(?![a-z])/g;

/** Finds all money amounts in folded text. */
export function findMoney(folded: string): MoneyMatch[] {
    const result: MoneyMatch[] = [];
    for (const m of Array.from(folded.matchAll(MONEY_RE))) {
        const base = parseCzechNumber(m[1] ?? "");
        if (base === null) continue;
        const index = m.index ?? 0;
        result.push({
            value: applyScale(base, m[2]),
            currency: m[3] === "kc" || m[3] === "czk" ? "CZK" : "EUR",
            index,
            context: folded.slice(Math.max(0, index - 80), index),
        });
    }
    return result;
}

/** Formats an amount for the user, e.g. "4 200 000 Kč" or "180 mil. Kč". */
export function formatMoney(value: number, currency: "CZK" | "EUR" = "CZK"): string {
    const unit = currency === "CZK" ? "Kč" : "EUR";
    if (value >= 10_000_000) {
        const millions = value / 1_000_000;
        return `${millions.toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} mil. ${unit}`;
    }
    return `${Math.round(value).toLocaleString("cs-CZ").replace(/\s/g, " ")} ${unit}`;
}

/** Converts "18 měsíců", "3 roky", "6 týdnů" to months (folded input). */
export function toMonths(amount: number, unit: string): number {
    if (/^(rok|roky|roku|let|leta)/.test(unit)) return amount * 12;
    if (/^(tyd|tyden)/.test(unit)) return amount / 4.33;
    if (/^(den|dn|dni)/.test(unit)) return amount / 30;
    return amount;
}

/** Converts a duration to weeks (folded unit). */
export function toWeeks(amount: number, unit: string): number {
    if (/^(mesic)/.test(unit)) return amount * 4.33;
    if (/^(den|dn|dni)/.test(unit)) return amount / 5;
    if (/^(rok|roky|let)/.test(unit)) return amount * 52;
    return amount;
}

export const truncate = (text: string, max: number): string =>
    text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
